import { KeyboardEvent, ReactNode, useEffect, useRef } from 'react';
import { X, MessageCircle, Search, Library } from 'lucide-react';
import { useOnboarding } from '../../hooks/useOnboarding';
import { getAllCollections } from '../../utils/institutions';
import { ICONO_COLECCION, FOCO } from '../biblioteca/piezas';

/**
 * Recorrido de primer ingreso: una sola ventana «Cómo funciona AlertAI» (maqueta aprobada
 * `docs/auditoria-ui-2026-10-19/maquetas/m4-bienvenida.html`, 19-10), sobre los tokens de DESIGN.md §2 y §11:
 * velo al 30 %, hoja de 640 px con radio 14, tres puntos con ícono en caja de 36 px y las cuatro bibliotecas reales.
 * Reemplaza (01-11-2026) al asistente de cinco pasos del diseño antiguo, que describía funciones retiradas
 * (bibliotecas por institución, búsqueda simple o inteligente, panel de filtros, notificaciones).
 *
 * Estados: primera vez (se abre solo) · reabierto desde «Guía de uso» · pospuesto (Ver después, X, Escape) ·
 * apagado («No volver a mostrar») · móvil (hoja al ancho con 16 px de margen, bibliotecas en una columna) ·
 * modo oscuro (solo clases de la paleta, DESIGN.md §10).
 */

const NOMBRE_BIBLIOTECA: Record<string, string> = {
  adm: 'Jurisprudencia administrativa del SII',
  jud: 'Sentencias del TTA',
  ley: 'Leyes y normas',
  conv: 'Convenios de doble tributación',
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

function Punto({ icono: Icono, titulo, children, primero = false }: { icono: typeof Search; titulo: string; children: ReactNode; primero?: boolean }) {
  return (
    <div className={`grid grid-cols-[36px_1fr] gap-3.5 px-6 py-4 ${primero ? '' : 'border-t border-slate-100'}`}>
      <span aria-hidden="true" className="w-9 h-9 rounded-[9px] bg-slate-100 text-slate-700 grid place-items-center">
        <Icono className="w-[15px] h-[15px]" strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <h3 className="text-[14px] font-semibold text-slate-900">{titulo}</h3>
        {children}
      </div>
    </div>
  );
}

export default function OnboardingWizard() {
  const { isOpen, close, complete, dontShowAgain, setDontShowAgain } = useOnboarding();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Al abrir, el foco entra a la ventana; al cerrar vuelve a quien la abrió (DESIGN.md §3). Escape la pospone.
  useEffect(() => {
    if (!isOpen) return;
    const previo = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) close(); };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      // «Guía de uso» vive en el menú de perfil, que se desmonta al elegirla: el foco ya cayó en <body> al abrir, así que
      // body no cuenta como «quien la abrió» y el foco vuelve al botón del menú.
      const conocido = previo && previo !== document.body && document.contains(previo);
      const destino = conocido ? previo : document.querySelector<HTMLElement>('button[aria-label="Menú de perfil"]');
      destino?.focus();
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  // Tab se queda dentro de la ventana mientras está abierta.
  const atraparTab = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) return;
    const primero = focusables[0];
    const ultimo = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === primero || document.activeElement === dialogRef.current)) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  };

  return (
    <>
      {/* Velo (DESIGN.md §11.4): `black` no cambia con el tema, así el velo oscurece también en modo oscuro */}
      <div className="fixed inset-0 z-50 bg-black/30 motion-safe:animate-fadeIn" onClick={close} aria-hidden="true" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="recorrido-titulo"
          aria-describedby="recorrido-subtitulo"
          tabIndex={-1}
          onKeyDown={atraparTab}
          /* Anillo con `ring-slate-200` (y no rgba fijo) para que en oscuro la hoja se separe del fondo (DESIGN.md §10, línea #2a2a2f) */
          className="pointer-events-auto w-full max-w-[640px] max-h-[calc(100vh-32px)] flex flex-col bg-white rounded-[14px] ring-1 ring-slate-200 shadow-[0_12px_32px_rgba(15,23,42,0.12)] motion-safe:animate-slideUp focus:outline-none"
        >
          {/* Cuerpo con scroll propio: el pie con «Empezar» queda siempre a la vista, también en pantallas bajas */}
          <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex items-start gap-3.5 px-6 pt-6 pb-1.5">
            <div className="flex-1 min-w-0">
              <h2 id="recorrido-titulo" className="text-[18px] font-semibold text-slate-900 tracking-[-0.01em]">Cómo funciona AlertAI</h2>
              <p id="recorrido-subtitulo" className="text-[13.5px] text-slate-500 mt-[3px] leading-[1.5]">
                Respuestas tributarias con la fuente a la vista. Tres cosas para empezar.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Cerrar"
              title="Cerrar (Esc)"
              className={`-mr-2.5 -mt-2 w-9 h-9 rounded-lg grid place-items-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 ${FOCO}`}
            >
              <X className="w-[15px] h-[15px]" strokeWidth={1.8} />
            </button>
          </div>

          <Punto icono={MessageCircle} titulo="Pregunta como se lo dirías a un colega" primero>
            <p className="text-[13.5px] leading-[1.55] text-slate-600 mt-[3px]">
              El asistente responde breve o en profundidad (Quick answer o Think deeper), y cada afirmación lleva su cita
              numerada{' '}
              <span className="inline-flex items-center justify-center w-[17px] h-[17px] rounded-[5px] bg-slate-100 text-slate-500 text-[10.5px] font-medium tabular-nums align-[-3px]">1</span>.
            </p>
          </Punto>

          <Punto icono={Search} titulo="Revisa la fuente sin perder el hilo">
            <p className="text-[13.5px] leading-[1.55] text-slate-600 mt-[3px]">
              Pasa el cursor sobre el número para leer el pasaje citado; un clic abre el documento completo.
            </p>
          </Punto>

          <Punto icono={Library} titulo="Busca por criterio o artículo en las bibliotecas">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-[18px] gap-y-1.5 mt-[9px]">
              {getAllCollections().map((c) => {
                const Icono = ICONO_COLECCION[c.key];
                return (
                  <li key={c.key} className="flex items-center gap-2 text-[13px] text-slate-700">
                    <Icono aria-hidden="true" className="w-[13px] h-[13px] shrink-0" strokeWidth={1.8} />
                    {NOMBRE_BIBLIOTECA[c.key] ?? c.label}
                  </li>
                );
              })}
            </ul>
          </Punto>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 px-6 py-3.5 border-t border-slate-100 bg-slate-50 rounded-b-[14px]">
            <label className="flex items-center gap-2 text-[13px] text-slate-600 cursor-pointer w-full sm:w-auto">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className={`w-[15px] h-[15px] rounded border-slate-300 accent-indigo-600 ${FOCO}`}
              />
              No volver a mostrar
            </label>
            <span className="flex-1" />
            <button
              type="button"
              onClick={close}
              className={`h-9 rounded-lg px-4 text-[13.5px] font-medium text-slate-600 hover:bg-slate-100 ${FOCO}`}
            >
              Ver después
            </button>
            <button
              type="button"
              onClick={complete}
              className={`h-9 rounded-lg px-4 text-[13.5px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 ${FOCO}`}
            >
              Empezar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
